import { useState, useEffect, useRef } from 'react';
import {
  Search,
  Calendar,
  X,
  Users,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  SlidersHorizontal,
  ChevronDown,
} from 'lucide-react';
import { FiltrosArquivo, GerenciadorOrigemFiltro } from '../../types/gerArquivos';

interface FiltrosArquivosProps {
  filtros: FiltrosArquivo;
  onFiltrosChange: (filtros: Partial<FiltrosArquivo>) => void;
  carregando?: boolean;
}

const CATEGORIA_CHIPS: ReadonlyArray<{ id: 1 | 2 | 3 | 4; label: string; short: string }> = [
  { id: 1, label: 'DTF Têxtil', short: 'DTF' },
  { id: 2, label: 'DTF UV', short: 'UV' },
  { id: 3, label: 'Sublimação', short: 'Sublim' },
  { id: 4, label: 'Camiseta', short: 'Camiseta' },
];

const ORIGEM_CHIPS: ReadonlyArray<{ id: GerenciadorOrigemFiltro; label: string }> = [
  { id: 'rascunho', label: 'Rascunho' },
  { id: 'pedido', label: 'Pedido' },
];

function chipClass(active: boolean, disabled: boolean, compact = false): string {
  const base = compact
    ? 'px-2 py-0.5 rounded-full text-[11px] font-medium border transition-all whitespace-nowrap flex-shrink-0'
    : 'px-2.5 py-1 rounded-full text-xs font-medium border transition-all whitespace-nowrap flex-shrink-0';
  if (disabled) {
    return `${base} opacity-50 cursor-not-allowed border-navy-600 text-gray-500 bg-navy-800/30`;
  }
  if (active) {
    return `${base} border-blue-400/60 bg-blue-500/20 text-blue-200`;
  }
  return `${base} border-navy-600 text-gray-300 bg-navy-800/50 hover:bg-navy-700 hover:text-white`;
}

function toolbarBtnClass(active = false, disabled = false): string {
  const base =
    'flex items-center justify-center gap-1 px-2 py-1.5 bg-navy-800/50 border border-navy-600 rounded-lg text-xs text-gray-300 transition-all flex-shrink-0';
  if (disabled) return `${base} opacity-50 cursor-not-allowed`;
  if (active) return `${base} border-blue-400/50 bg-blue-500/10 text-blue-200`;
  return `${base} hover:bg-navy-700 hover:text-white`;
}

export function FiltrosArquivos({
  filtros,
  onFiltrosChange,
  carregando = false,
}: FiltrosArquivosProps) {
  const [localSearchTerm, setLocalSearchTerm] = useState(filtros.searchTerm);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalSearchTerm(filtros.searchTerm);
  }, [filtros.searchTerm]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (localSearchTerm !== filtros.searchTerm) {
      debounceTimerRef.current = setTimeout(() => {
        onFiltrosChange({ searchTerm: localSearchTerm });
      }, 400);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [localSearchTerm, filtros.searchTerm, onFiltrosChange]);

  const handleSearchChange = (searchTerm: string) => {
    setLocalSearchTerm(searchTerm);
  };

  const handleDateFromChange = (dateFrom: string) => {
    onFiltrosChange({ dateFrom });
  };

  const handleDateToChange = (dateTo: string) => {
    onFiltrosChange({ dateTo });
  };

  const handleShowAllUsersChange = (showAllUsers: boolean) => {
    onFiltrosChange({ showAllUsers });
  };

  const handleToggleCategoria = (id: 1 | 2 | 3 | 4) => {
    onFiltrosChange({
      prod_cat_item: filtros.prod_cat_item === id ? undefined : id,
    });
  };

  const handleToggleOrigem = (id: GerenciadorOrigemFiltro) => {
    onFiltrosChange({
      origem: filtros.origem === id ? undefined : id,
    });
  };

  const isMaisRecentes = filtros.sort_dir !== 'asc';
  const handleToggleSortDir = () => {
    onFiltrosChange({
      sort_by: 'created_at',
      sort_dir: isMaisRecentes ? 'asc' : 'desc',
    });
  };

  const limparFiltros = () => {
    onFiltrosChange({
      searchTerm: '',
      dateFrom: '',
      dateTo: '',
      showAllUsers: false,
      sort_by: 'created_at',
      sort_dir: 'desc',
      prod_cat_item: undefined,
      origem: undefined,
    });
    setLocalSearchTerm('');
  };

  const hasChipFilters =
    filtros.prod_cat_item !== undefined || filtros.origem !== undefined;
  const hasAdvancedFilters =
    Boolean(filtros.dateFrom || filtros.dateTo) ||
    filtros.showAllUsers ||
    !isMaisRecentes;
  const temFiltrosAtivos =
    localSearchTerm || hasChipFilters || hasAdvancedFilters;

  const advancedBadgeCount = [
    Boolean(filtros.dateFrom || filtros.dateTo),
    filtros.showAllUsers,
    !isMaisRecentes,
  ].filter(Boolean).length;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar cliente, pedido ou arquivo..."
          value={localSearchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          disabled={carregando}
          className="w-full pl-8 pr-3 py-1.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:border-transparent text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Buscar arquivos"
        />
      </div>

      <div
        className="-mx-1 px-1 flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-thin scrollbar-thumb-navy-600"
        role="group"
        aria-label="Filtros rápidos de origem e categoria"
      >
        {ORIGEM_CHIPS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => handleToggleOrigem(chip.id)}
            disabled={carregando}
            className={chipClass(filtros.origem === chip.id, carregando, true)}
            aria-pressed={filtros.origem === chip.id}
            title={`Origem: ${chip.label}`}
          >
            {chip.label}
          </button>
        ))}
        <span className="w-px self-stretch bg-navy-600 flex-shrink-0 my-0.5" aria-hidden />
        {CATEGORIA_CHIPS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => handleToggleCategoria(chip.id)}
            disabled={carregando}
            className={chipClass(filtros.prod_cat_item === chip.id, carregando, true)}
            aria-pressed={filtros.prod_cat_item === chip.id}
            title={chip.label}
          >
            {chip.short}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setAdvancedOpen((open) => !open)}
          disabled={carregando}
          className={`${toolbarBtnClass(advancedOpen || hasAdvancedFilters, carregando)} md:hidden flex-1 min-w-0`}
          aria-expanded={advancedOpen}
          aria-controls="ger-arq-filtros-avancados"
        >
          <SlidersHorizontal className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">Datas e opções</span>
          {advancedBadgeCount > 0 && (
            <span className="ml-0.5 min-w-[1rem] h-4 px-1 rounded-full bg-blue-500/30 text-blue-200 text-[10px] font-semibold leading-4 text-center">
              {advancedBadgeCount}
            </span>
          )}
          <ChevronDown
            className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${advancedOpen ? 'rotate-180' : ''}`}
          />
        </button>

        <button
          type="button"
          onClick={handleToggleSortDir}
          disabled={carregando}
          className={toolbarBtnClass(!isMaisRecentes, carregando)}
          aria-label={`Ordenar por ${isMaisRecentes ? 'mais recentes' : 'mais antigos'}`}
          title={isMaisRecentes ? 'Mais recentes' : 'Mais antigos'}
        >
          {isMaisRecentes ? (
            <ArrowDownWideNarrow className="w-3.5 h-3.5" />
          ) : (
            <ArrowUpWideNarrow className="w-3.5 h-3.5" />
          )}
          <span className="hidden sm:inline font-medium text-blue-300">
            {isMaisRecentes ? 'Recentes' : 'Antigos'}
          </span>
        </button>

        <button
          type="button"
          onClick={() => handleShowAllUsersChange(!filtros.showAllUsers)}
          disabled={carregando}
          className={toolbarBtnClass(Boolean(filtros.showAllUsers), carregando)}
          aria-pressed={Boolean(filtros.showAllUsers)}
          aria-label="Mostrar arquivos de todos os usuários"
          title="Todos os usuários"
        >
          <Users className="w-3.5 h-3.5" />
        </button>

        {temFiltrosAtivos && (
          <button
            type="button"
            onClick={limparFiltros}
            disabled={carregando}
            className={toolbarBtnClass(false, carregando)}
            aria-label="Limpar filtros"
            title="Limpar filtros"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div
        id="ger-arq-filtros-avancados"
        className={`space-y-2 ${advancedOpen ? 'block' : 'hidden'} md:block`}
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] text-gray-400 mb-0.5">
              <Calendar className="w-3 h-3 inline mr-0.5" />
              Data inicial
            </label>
            <input
              type="date"
              value={filtros.dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              disabled={carregando}
              className="w-full px-2 py-1 bg-navy-800 border border-navy-600 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Data inicial"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 mb-0.5">
              <Calendar className="w-3 h-3 inline mr-0.5" />
              Data final
            </label>
            <input
              type="date"
              value={filtros.dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              disabled={carregando}
              className="w-full px-2 py-1 bg-navy-800 border border-navy-600 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Data final"
            />
          </div>
        </div>

        <div className="hidden md:grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleToggleSortDir}
            disabled={carregando}
            className="flex items-center justify-between gap-1.5 px-2.5 py-1.5 bg-navy-800/50 border border-navy-600 rounded-lg text-xs text-gray-300 hover:bg-navy-700 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={`Ordenar por ${isMaisRecentes ? 'mais recentes' : 'mais antigos'}`}
          >
            {isMaisRecentes ? (
              <ArrowDownWideNarrow className="w-3.5 h-3.5 flex-shrink-0" />
            ) : (
              <ArrowUpWideNarrow className="w-3.5 h-3.5 flex-shrink-0" />
            )}
            <span className="font-medium text-blue-300 truncate">
              {isMaisRecentes ? 'Mais recentes' : 'Mais antigos'}
            </span>
          </button>

          <label
            htmlFor="showAllUsers"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 bg-navy-800/50 border border-navy-600 rounded-lg text-xs text-gray-300 cursor-pointer select-none transition-colors hover:bg-navy-700 ${
              carregando ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <input
              type="checkbox"
              id="showAllUsers"
              checked={filtros.showAllUsers || false}
              onChange={(e) => handleShowAllUsersChange(e.target.checked)}
              disabled={carregando}
              className="w-3.5 h-3.5 text-blue-600 bg-navy-700 border-navy-500 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              aria-label="Mostrar arquivos de todos os usuários"
            />
            <Users className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">Todos os usuários</span>
          </label>
        </div>
      </div>
    </div>
  );
}
